import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import config from './Config.json';

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled Components
const BackgroundBox = styled(Box)({
  position: 'relative',
  minHeight: '100vh',
  background: 'linear-gradient(180deg, #000000 0%, #0a0a0a 50%, #000000 100%)',
  overflow: 'hidden',
  paddingTop: '80px',
  paddingBottom: '80px',
});

const GradientOrb = styled(Box)(({ top, left, bottom, right, color }) => ({
  position: 'absolute',
  width: 500,
  height: 500,
  borderRadius: '50%',
  background: color,
  filter: 'blur(100px)',
  pointerEvents: 'none',
  top,
  left,
  bottom,
  right,
}));

const GridPattern = styled(Box)({
  position: 'absolute',
  inset: 0,
  opacity: 0.05,
  backgroundImage: `linear-gradient(rgba(148, 163, 184, 0.1) 1px, transparent 1px),
                     linear-gradient(90deg, rgba(148, 163, 184, 0.1) 1px, transparent 1px)`,
  backgroundSize: '50px 50px',
  pointerEvents: 'none',
});

const StyledCard = styled(Card)({
  height: '100%',
  background: 'rgba(0, 0, 0, 0.7)',
  backdropFilter: 'blur(20px)',
  borderRadius: 16,
  border: '1px solid rgba(193, 227, 40, 0.3)',
  overflow: 'hidden',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    transform: 'translateY(-8px)',
    borderColor: 'rgba(193, 227, 40, 0.5)',
    boxShadow: '0 20px 40px rgba(193, 227, 40, 0.2)',
    '& .card-image': {
      transform: 'scale(1.1)',
    },
  },
});

const StyledCardMedia = styled(CardMedia)({
  height: 280,
  position: 'relative',
  overflow: 'hidden',
  '& img': {
    transition: 'transform 0.5s ease',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '50%',
    background: 'linear-gradient(to top, rgba(0, 0, 0, 0.8) 0%, transparent 100%)',
  },
});

function CardPage() {
  const cardArr = config;

  return (
    <BackgroundBox>
      {/* Background Elements */}
      <GradientOrb top="20%" left={-256} color="rgba(193, 227, 40, 0.1)" />
      <GradientOrb bottom="20%" right={-256} color="rgba(193, 227, 40, 0.1)" />
      <GridPattern />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Section Header */}
        <Box textAlign="center" mb={8}>

          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              fontWeight: 700,
              color: '#fff',
              letterSpacing: -1,
              mb: 2,
            }}
          >
            Industry{' '}
            <Box
              component="span"
              sx={{
                background: 'linear-gradient(135deg, #c1e328 0%, #9fcc2e 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Applications
            </Box>
          </Typography>

          <Typography
            variant="h6"
            sx={{
              color: 'rgba(148, 163, 184, 1)',
              maxWidth: 700,
              mx: 'auto',
              lineHeight: 1.7,
              fontSize: { xs: '1rem', md: '1.125rem' },
            }}
          >
            Versatile robotic solutions for diverse industries
          </Typography>
        </Box>

        {/* Cards Grid */}
        <Grid container spacing={4}>
          {cardArr.map((eachCard, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Box
                sx={{
                  animation: `${fadeInUp} 0.6s ease-out ${index * 0.1}s both`,
                  height: '100%',
                }}
              >
                <StyledCard elevation={0}>
                  <StyledCardMedia>
                    <Box
                      component="img"
                      className="card-image"
                      src={require('./images/' + eachCard.img + '.jpg')}
                      alt={eachCard.title}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.5s ease',
                      }}
                    />
                  </StyledCardMedia>

                  <CardContent
                    sx={{
                      p: 3,
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#fff',
                        fontWeight: 700,
                        mb: 1.5,
                        fontSize: '1.125rem',
                      }}
                    >
                      {eachCard.title}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(148, 163, 184, 1)',
                        lineHeight: 1.7,
                        fontSize: '0.875rem',
                      }}
                    >
                      {eachCard.text}
                    </Typography>
                  </CardContent>
                </StyledCard>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </BackgroundBox>
  );
}

export default CardPage;